
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateRange } from "react-day-picker";
import { Check, X } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { ProjectMilestones } from "@/components/ProjectMilestones";
import { ProjectDetailsCard } from "@/components/project/ProjectDetailsCard";
import { TeamMembersCard } from "@/components/project/TeamMembersCard";
import { parsePlainDateString } from "@/utils/dateTransforms";

export default function Project() {
  const { projectId } = useParams();
  const { toast } = useToast();
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  // Convert string IDs to numbers
  const projectIdNum = projectId ? parseInt(projectId) : undefined;

  const { data: project, isLoading, refetch } = useQuery({
    queryKey: ['project', projectIdNum],
    queryFn: async () => {
      if (!projectIdNum) throw new Error('Project ID is required');
      
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          client:clients (
            name,
            legal_name
          ),
          milestones (
            id,
            description,
            amount,
            currency,
            due_date,
            status
          ),
          employee_project_assignments (
            id,
            hourly_rate,
            currency,
            start_date,
            end_date,
            employee:employees (
              id,
              name,
              role
            )
          )
        `)
        .eq('id', projectIdNum)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        setDateRange({
          from: data.start_date ? new Date(data.start_date + 'T12:00:00') : undefined,
          to: data.end_date ? new Date(data.end_date + 'T12:00:00') : undefined
        });
      }
      
      return data;
    },
    enabled: !!projectIdNum
  });

  // Fetch the board for this project
  const { data: board } = useQuery({
    queryKey: ['project-board', projectIdNum],
    queryFn: async () => {
      if (!projectIdNum) return null;
      
      const { data, error } = await supabase
        .from('boards')
        .select('id')
        .eq('project_id', projectIdNum)
        .maybeSingle();

      if (error) {
        console.error('Error fetching board:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!projectIdNum
  });

  const updateProjectName = async (newName: string) => {
    if (!projectIdNum) return;

    const { error } = await supabase
      .from('projects')
      .update({ name: newName })
      .eq('id', projectIdNum);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update project name",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Project name updated successfully",
    });
    setIsEditingName(false);
    refetch();
  };

  const handleDateRangeChange = async (range: DateRange | undefined) => {
    if (!projectIdNum || !range) return;

    const { error } = await supabase
      .from('projects')
      .update({
        start_date: range.from?.toISOString().split('T')[0] || null,
        end_date: range.to?.toISOString().split('T')[0] || null
      })
      .eq('id', projectIdNum);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update project dates",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Project dates updated successfully",
    });
    setDateRange(range);
    refetch();
  };

  if (isLoading) {
    return (
      <div className="p-8 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold mb-4">Project not found</h1>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <Input
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                className="text-3xl font-bold h-12"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") updateProjectName(nameValue);
                  if (e.key === "Escape") setIsEditingName(false);
                }}
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => updateProjectName(nameValue)}
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsEditingName(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <h1
              className="text-3xl font-bold cursor-pointer hover:bg-accent hover:text-accent-foreground p-2 rounded-md -ml-2"
              onClick={() => {
                setNameValue(project.name);
                setIsEditingName(true);
              }}
            >
              {project.name}
            </h1>
          )}
          <p className="text-muted-foreground mt-1">
            Client: {project.client?.name}
          </p>
        </div>
        <Button variant={project.status === 'active' ? 'default' : 'secondary'}>
          {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ProjectDetailsCard 
          project={project}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          refetch={refetch}
          boardId={board?.id || null}
        />
        
        <TeamMembersCard 
          assignments={(project.employee_project_assignments || []).map(assignment => ({
            ...assignment,
            start_date: parsePlainDateString(assignment.start_date),
            end_date: parsePlainDateString(assignment.end_date),
          }))}
          projectType={project.type}
        />

        {project.type === 'fixed_price' && (
          <ProjectMilestones 
            projectId={projectIdNum!}
            milestones={project.milestones || []}
            projectType={project.type || ''}
          />
        )}
      </div>
    </div>
  );
}
